import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  DocumentTextIcon,
  FolderIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useBranch } from '../contexts/BranchContext';
import api from '../services/api';
import PermissionGate from './PermissionGate';

interface DocumentType {
  id: number;
  organizationId: number;
  name: string;
  description?: string;
  isRequired: boolean;
  maxFileSizeMb: number;
  allowedFileTypes: string[];
  createdAt: string;
  updatedAt: string;
}

interface DocumentTypeGroup {
  id: number;
  name: string;
  description?: string;
  color: string;
  document_types: { id: number; name: string }[];
}

interface DocumentSettingsModalProps {
  onClose: () => void;
  onDocumentTypeUpdated: () => void;
}

const DocumentSettingsModal: React.FC<DocumentSettingsModalProps> = ({ 
  onClose, 
  onDocumentTypeUpdated 
}) => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [activeTab, setActiveTab] = useState<'types' | 'groups'>('types');
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [documentGroups, setDocumentGroups] = useState<DocumentTypeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingType, setEditingType] = useState<DocumentType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isRequired: false,
    maxFileSizeMb: 10,
    allowedFileTypes: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']
  });
  
  // Group form state
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DocumentTypeGroup | null>(null);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    documentTypeIds: [] as number[]
  });

  useEffect(() => {
    if (!branchLoading) {
      fetchDocumentTypes();
      fetchDocumentGroups();
    }
  }, [currentBranch, isViewingAllBranches, branchLoading]);

  const fetchDocumentTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/documents/types');
      setDocumentTypes(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching document types:', err);
      setError('Failed to fetch document types');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentGroups = async () => {
    try {
      const response = await api.get('/documents/groups');
      setDocumentGroups(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching document groups:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      
      if (editingType) {
        // Update existing document type
        await import('../services/api').then(module => 
          module.default.put(`/documents/types/${editingType.id}`, formData)
        );
      } else {
        // Create new document type
        await import('../services/api').then(module => 
          module.default.post('/documents/types', formData)
        );
      }

      await fetchDocumentTypes();
      onDocumentTypeUpdated();
      resetForm();
    } catch (err: any) {
      console.error('Error saving document type:', err);
      setError(err.response?.data?.message || 'Failed to save document type');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this document type?')) {
      return;
    }

    try {
      setError(null);
      await import('../services/api').then(module => 
        module.default.delete(`/documents/types/${id}`)
      );
      await fetchDocumentTypes();
      onDocumentTypeUpdated();
    } catch (err: any) {
      console.error('Error deleting document type:', err);
      setError(err.response?.data?.message || 'Failed to delete document type');
    }
  };

  const handleEdit = (type: DocumentType) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      isRequired: type.isRequired,
      maxFileSizeMb: type.maxFileSizeMb,
      allowedFileTypes: [...type.allowedFileTypes]
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      isRequired: false,
      maxFileSizeMb: 10,
      allowedFileTypes: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']
    });
    setEditingType(null);
    setShowAddForm(false);
  };

  const addFileType = (type: string) => {
    if (type.trim() && !formData.allowedFileTypes.includes(type.trim().toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        allowedFileTypes: [...prev.allowedFileTypes, type.trim().toLowerCase()]
      }));
    }
  };

  const removeFileType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      allowedFileTypes: prev.allowedFileTypes.filter(t => t !== type)
    }));
  };

  const commonFileTypes = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'gif', 'txt', 'xlsx', 'pptx'];
  const groupColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

  // Group handlers
  const handleGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      
      if (editingGroup) {
        await api.put(`/documents/groups/${editingGroup.id}`, groupFormData);
      } else {
        await api.post('/documents/groups', groupFormData);
      }

      await fetchDocumentGroups();
      resetGroupForm();
    } catch (err: any) {
      console.error('Error saving document group:', err);
      setError(err.response?.data?.message || 'Failed to save document group');
    }
  };

  const handleGroupDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    
    try {
      setError(null);
      await api.delete(`/documents/groups/${id}`);
      await fetchDocumentGroups();
    } catch (err: any) {
      console.error('Error deleting document group:', err);
      setError(err.response?.data?.message || 'Failed to delete document group');
    }
  };

  const handleGroupEdit = (group: DocumentTypeGroup) => {
    setEditingGroup(group);
    setGroupFormData({
      name: group.name,
      description: group.description || '',
      color: group.color,
      documentTypeIds: group.document_types.map(dt => dt.id)
    });
    setShowGroupForm(true);
  };

  const resetGroupForm = () => {
    setGroupFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      documentTypeIds: []
    });
    setEditingGroup(null);
    setShowGroupForm(false);
  };

  const toggleGroupDocType = (docTypeId: number) => {
    setGroupFormData(prev => ({
      ...prev,
      documentTypeIds: prev.documentTypeIds.includes(docTypeId)
        ? prev.documentTypeIds.filter(id => id !== docTypeId)
        : [...prev.documentTypeIds, docTypeId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Document Settings</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          <button
            onClick={() => setActiveTab('types')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === 'types' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <DocumentTextIcon className="w-4 h-4 inline mr-1.5" />
            Document Types
          </button>
          <button
            onClick={() => setActiveTab('groups')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === 'groups' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FolderIcon className="w-4 h-4 inline mr-1.5" />
            Groups ({documentGroups.length})
          </button>
        </div>

        <div className="space-y-6">
          {/* Document Types Tab */}
          {activeTab === 'types' && (
            <>
              {/* Add/Edit Form */}
              {showAddForm && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-medium text-gray-900 mb-4">
                {editingType ? 'Edit Document Type' : 'Add New Document Type'}
              </h4>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Driver's License"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max File Size (MB)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.maxFileSizeMb}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxFileSizeMb: parseInt(e.target.value) || 10 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional description for this document type"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allowed File Types
                  </label>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {formData.allowedFileTypes.map((type, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {type}
                          <button
                            type="button"
                            onClick={() => removeFileType(type)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {commonFileTypes
                        .filter(type => !formData.allowedFileTypes.includes(type))
                        .map((type, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => addFileType(type)}
                            className="px-2 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded"
                          >
                            + {type}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isRequired"
                    checked={formData.isRequired}
                    onChange={(e) => setFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isRequired" className="ml-2 block text-sm text-gray-700">
                    Required document type
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {editingType ? 'Update' : 'Create'} Document Type
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Document Types List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-medium text-gray-900">Document Types</h4>
              {!showAddForm && (
                <PermissionGate module="documents" permission="create">
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add Document Type
                  </button>
                </PermissionGate>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : documentTypes.length === 0 ? (
              <div className="text-center py-8">
                <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No document types found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documentTypes.map((type) => (
                  <div key={type.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h5 className="text-sm font-medium text-gray-900">{type.name}</h5>
                          {type.isRequired && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Required
                            </span>
                          )}
                        </div>
                        {type.description && (
                          <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>Max size: {type.maxFileSizeMb}MB</span>
                          <span>Types: {type.allowedFileTypes.join(', ')}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <PermissionGate module="documents" permission="edit">
                          <button
                            onClick={() => handleEdit(type)}
                            className="p-2 text-gray-400 hover:text-gray-600"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate module="documents" permission="delete">
                          <button
                            onClick={() => handleDelete(type.id)}
                            className="p-2 text-gray-400 hover:text-red-600"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
            </>
          )}

          {/* Groups Tab */}
          {activeTab === 'groups' && (
            <>
              {/* Add/Edit Group Form */}
              {showGroupForm && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-4">
                    {editingGroup ? 'Edit Group' : 'Create New Group'}
                  </h4>
                  
                  <form onSubmit={handleGroupSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Group Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={groupFormData.name}
                          onChange={(e) => setGroupFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Initial Documents"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Color
                        </label>
                        <div className="flex gap-2">
                          {groupColors.map(color => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => setGroupFormData(prev => ({ ...prev, color }))}
                              className={`w-8 h-8 rounded-full border-2 transition ${
                                groupFormData.color === color ? 'border-gray-900 scale-110' : 'border-transparent'
                              }`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={groupFormData.description}
                        onChange={(e) => setGroupFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Optional description"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Document Types in Group ({groupFormData.documentTypeIds.length} selected)
                      </label>
<div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 border rounded-md bg-white">
                        {documentTypes.map(dt => (
                          <label
                            key={dt.id}
                            onClick={() => toggleGroupDocType(dt.id)}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer transition ${
                              groupFormData.documentTypeIds.includes(dt.id)
                                ? 'bg-blue-50 border border-blue-200'
                                : 'hover:bg-gray-50 border border-transparent'
                            }`}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                              groupFormData.documentTypeIds.includes(dt.id)
                                ? 'bg-blue-600 border-blue-600'
                                : 'border-gray-300'
                            }`}>
                              {groupFormData.documentTypeIds.includes(dt.id) && (
                                <CheckIcon className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <span className="text-sm truncate">{dt.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={resetGroupForm}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        {editingGroup ? 'Update' : 'Create'} Group
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Groups List */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-gray-900">Document Groups</h4>
                  {!showGroupForm && (
                    <PermissionGate module="documents" permission="create">
                      <button
                        onClick={() => setShowGroupForm(true)}
                        className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Create Group
                      </button>
                    </PermissionGate>
                  )}
                </div>

                {documentGroups.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">No document groups yet</p>
                    <p className="text-sm text-gray-500">
                      Create groups to quickly select multiple document types during requests or uploads.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documentGroups.map((group) => (
                      <div key={group.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: group.color }}
                              />
                              <h5 className="text-sm font-medium text-gray-900">{group.name}</h5>
                              <span className="text-xs text-gray-500">
                                ({group.document_types.length} types)
                              </span>
                            </div>
                            {group.description && (
                              <p className="text-sm text-gray-600 mb-2">{group.description}</p>
                            )}
                            <div className="flex flex-wrap gap-1">
                              {group.document_types.map(dt => (
                                <span 
                                  key={dt.id}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                                >
                                  {dt.name}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <PermissionGate module="documents" permission="edit">
                              <button
                                onClick={() => handleGroupEdit(group)}
                                className="p-2 text-gray-400 hover:text-gray-600"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate module="documents" permission="delete">
                              <button
                                onClick={() => handleGroupDelete(group.id)}
                                className="p-2 text-gray-400 hover:text-red-600"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentSettingsModal;
